import { useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsTelephone, BsCheckCircleFill, BsXLg } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import { useErrorStore } from '#lib/stores/errorStore';

import { useCreateActivity, useUpdateActivity } from '../../../../models/activities/mutations';

import { SubModeToggle, nowLocal } from './shared';

const buildCallPayload = ({
	isEdit,
	subMode,
	autoTitle,
	body,
	dueAt,
	selectedContactId,
	reached,
	companyId,
	dealId,
	channelTypeId,
	initialData,
}) => {
	if (isEdit) {
		const payload = {
			title: autoTitle,
			body: body || null,
			dueAt: subMode === 'todo' && dueAt ? new Date(dueAt).toISOString() : null,
			crmContactId: selectedContactId || null,
		};
		if (reached !== null) {
			payload.channelPayload = { ...(initialData.channelPayload ?? {}), reached };
		}
		return payload;
	}

	const now = new Date().toISOString();
	const base = {
		crmCompanyId: companyId,
		...(dealId && { dealId }),
		crmContactId: selectedContactId || null,
	};

	if (subMode === 'log') {
		return {
			...base,
			activityType: 'communication',
			channelTypeId: channelTypeId ?? null,
			title: autoTitle,
			body: body || null,
			status: 'completed',
			completedAt: now,
			channelPayload: { direction: 'outbound', reached },
		};
	}

	return {
		...base,
		activityType: 'task',
		channelTypeId: channelTypeId ?? null,
		title: autoTitle,
		body: body || null,
		status: 'planned',
		dueAt: dueAt ? new Date(dueAt).toISOString() : null,
	};
};

const CallForm = ({ companyId, dealId, contacts = [], onClose, initialData, channelTypeId }) => {
	const isEdit = Boolean(initialData);
	const [subMode, setSubMode] = useState(() =>
		isEdit ? (initialData.status === 'completed' ? 'log' : 'todo') : 'todo',
	);
	const [selectedContactId, setSelectedContactId] = useState(initialData?.crmContactId ?? '');
	const [dueAt, setDueAt] = useState(
		initialData?.dueAt ? new Date(initialData.dueAt).toISOString().slice(0, 16) : '',
	);
	const [body, setBody] = useState(initialData?.body ?? '');
	const [reached, setReached] = useState(initialData?.channelPayload?.reached ?? null);
	const createActivity = useCreateActivity();
	const updateActivity = useUpdateActivity();
	const showError = useErrorStore((s) => s.showError);

	useEffect(() => {
		if (selectedContactId || contacts.length === 0) return;
		const primary = contacts.find((c) => c.isPrimary);
		setSelectedContactId(primary?.id ?? (contacts.length === 1 ? contacts[0].id : ''));
	}, [contacts, selectedContactId]);

	const selectedContact = useMemo(
		() => contacts.find((c) => c.id === selectedContactId),
		[contacts, selectedContactId],
	);

	const contactDisplayName = (c) =>
		c.fullName ?? (`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.emailAddress || 'Unbenannt');

	const buildAutoTitle = () => {
		const name = selectedContact ? contactDisplayName(selectedContact) : null;
		return name ? `Anruf: ${name}` : 'Anruf';
	};

	const handleSubmit = async () => {
		if (subMode === 'log' && reached === null) return;
		const autoTitle = buildAutoTitle();
		const payload = buildCallPayload({
			isEdit,
			subMode,
			autoTitle,
			body,
			dueAt,
			selectedContactId,
			reached,
			companyId,
			dealId,
			channelTypeId,
			initialData,
		});

		try {
			if (isEdit) {
				await updateActivity.mutateAsync({ id: initialData.id, payload });
			} else {
				await createActivity.mutateAsync(payload);
			}
			onClose();
		} catch (err) {
			showError({ error: err, title: isEdit ? 'Speichern fehlgeschlagen' : 'Fehler beim Speichern' });
		}
	};

	const isPending = isEdit ? updateActivity.isPending : createActivity.isPending;

	const contactSelect = (
		<Select value={selectedContactId} onChange={(e) => setSelectedContactId(e.target.value)}>
			<option value="">Kontakt wählen (optional)…</option>
			{contacts.map((c) => (
				<option key={c.id} value={c.id}>
					{contactDisplayName(c)}
					{c.phoneNumber ? ` (${c.phoneNumber})` : ' (keine Nummer)'}
				</option>
			))}
		</Select>
	);

	return (
		<div className="p-3 space-y-2.5">
			<div className="flex items-center gap-2">
				<div className="flex items-center gap-1.5 px-2 py-1 rounded-control bg-primary-100 text-primary-600 text-xs font-medium">
					<BsTelephone className="w-4 h-4" /> Anruf
				</div>
				{!isEdit && <SubModeToggle value={subMode} onChange={setSubMode} />}
				<IconButton icon={BsXLg} label="Schließen" onClick={onClose} className="ml-auto h-6 w-6" />
			</div>
			{contactSelect}
			{subMode === 'todo' ? (
				<>
					<TextInput
						type="datetime-local"
						value={dueAt}
						onChange={(e) => setDueAt(e.target.value)}
						min={nowLocal()}
						className="!w-auto"
					/>
					<Textarea
						value={body}
						onChange={(e) => setBody(e.target.value)}
						placeholder="Notiz (optional)"
						rows={2}
						className="!resize-none"
					/>
				</>
			) : (
				<>
					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => setReached(true)}
							className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-control border text-xs font-medium transition-colors ${
								reached === true
									? 'bg-success-200 border-success-500/40 text-success-700'
									: 'bg-white border-bgBase-500 text-bgBase-800 hover:border-bgBase-600'
							}`}
						>
							<BsCheckCircleFill className="w-3.5 h-3.5" /> Erreicht
						</button>
						<button
							type="button"
							onClick={() => setReached(false)}
							className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-control border text-xs font-medium transition-colors ${
								reached === false
									? 'bg-caution-200 border-caution-500/40 text-caution-700'
									: 'bg-white border-bgBase-500 text-bgBase-800 hover:border-bgBase-600'
							}`}
						>
							<BsXLg className="w-3 h-3" /> Nicht erreicht
						</button>
					</div>
					<Textarea
						value={body}
						onChange={(e) => setBody(e.target.value)}
						placeholder="Notiz …"
						rows={2}
						className="!resize-none"
					/>
				</>
			)}
			<div className="flex justify-end gap-2 pt-1">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button
					variant="solid"
					size="sm"
					onClick={handleSubmit}
					loading={isPending}
					disabled={subMode === 'log' ? reached === null : false}
				>
					Speichern
				</Button>
			</div>
		</div>
	);
};
CallForm.propTypes = {
	companyId: PropTypes.string,
	dealId: PropTypes.string,
	contacts: PropTypes.array,
	onClose: PropTypes.func,
	initialData: PropTypes.object,
	channelTypeId: PropTypes.string,
};

export default CallForm;
